import React from "react";
import { ArrowUp, ArrowDown, GripVertical, Trash2 } from "lucide-react";

/**
 * ReorderList — universal reorderable list with up/down buttons + optional delete.
 * Simpler than dnd-kit for the many CMS lists that need it.
 *
 * Props:
 *   items: array
 *   onChange: (newArray) => void
 *   renderItem: (item, index, controls) => ReactNode  — `controls` is a ready-to-use JSX with up/down/delete
 *   getKey?: (item, i) => string
 *   allowDelete?: boolean
 *   compact?: boolean
 */
export default function ReorderList({ items = [], onChange, renderItem, getKey, allowDelete = true, compact = false }) {
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= items.length) return;
    const arr = [...items];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    onChange(arr);
  };
  const del = (i) => {
    if (!window.confirm("Remover este item?")) return;
    onChange(items.filter((_, k) => k !== i));
  };

  return (
    <div className={compact ? "space-y-1" : "space-y-2"}>
      {items.map((it, i) => {
        const key = getKey ? getKey(it, i) : (it?.id || it?.key || i);
        const controls = (
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => move(i, -1)} disabled={i === 0} title="Mover para cima" className="text-neutral-400 hover:text-black disabled:opacity-30" data-testid={`reorder-up-${i}`}>
              <ArrowUp className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => move(i, 1)} disabled={i === items.length - 1} title="Mover para baixo" className="text-neutral-400 hover:text-black disabled:opacity-30" data-testid={`reorder-down-${i}`}>
              <ArrowDown className="w-3.5 h-3.5" />
            </button>
            {allowDelete && (
              <button onClick={() => del(i)} title="Remover" className="text-red-500 hover:text-red-700" data-testid={`reorder-delete-${i}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
        return (
          <div key={key} className={compact ? "flex items-center gap-2" : "flex items-start gap-2"} data-testid={`reorder-row-${i}`}>
            <GripVertical className="w-3.5 h-3.5 text-neutral-400 shrink-0 mt-1" />
            <div className="flex-1 min-w-0">{renderItem(it, i, controls)}</div>
            {controls}
          </div>
        );
      })}
    </div>
  );
}
